import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type {
  KakaoTemplateType,
  TemplateApprovalStatus,
  TemplateCreatePayload,
  TemplateDetail,
  TemplateListFilter,
  TemplateListItem,
  TemplateListPage,
  TemplateStatus,
  TemplateUpdatePayload,
} from '../types/notificationTemplate';
import { fromTemplateButtons } from '../lib/kakaoTemplate';

/** BE TemplateButtonView wire shape(파싱용). */
type ButtonWire = { name?: string | null; type?: string | null; urlMobile?: string | null; urlPc?: string | null };

const BASE = '/admin/v1/notification/templates';

interface Envelope<T> {
  success: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

const isConflictError = (status: number, code: string | undefined): boolean =>
  status === 409 || code === 'COMMON_CONFLICT';

const isNotFoundError = (status: number, code: string | undefined): boolean =>
  status === 404 || code === 'COMMON_NOT_FOUND';

class TemplateApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'TemplateApiError';
    this.status = status;
    this.code = code;
  }

  isConflict(): boolean {
    return isConflictError(this.status, this.code);
  }

  isNotFound(): boolean {
    return isNotFoundError(this.status, this.code);
  }
}

export { TemplateApiError };

const readEnvelope = async <T,>(res: Response, fallbackMsg: string): Promise<T> => {
  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!res.ok || !env || env.success !== true) {
    const code = env?.error?.code;
    const msg = env?.error?.message ?? fallbackMsg;
    throw new TemplateApiError(res.status, code, msg);
  }
  if (env.data == null) {
    throw new TemplateApiError(res.status, env.error?.code, fallbackMsg);
  }
  return env.data;
};

const buildListQs = (filter: TemplateListFilter): string => {
  const qs = new URLSearchParams({
    page: String(filter.page),
    size: String(filter.size),
    sort: 'templateCode,asc',
  });
  if (filter.q && filter.q.trim()) qs.set('q', filter.q.trim());
  if (filter.channel) qs.set('channel', filter.channel);
  if (filter.languageCode) qs.set('languageCode', filter.languageCode);
  if (filter.kakaoTemplateType) qs.set('kakaoTemplateType', filter.kakaoTemplateType.toUpperCase());
  if (filter.approvalStatus) qs.set('approvalStatus', filter.approvalStatus.toUpperCase());
  return qs.toString();
};

const KAKAO_TYPES: ReadonlyArray<KakaoTemplateType> = ['basic', 'emphasis', 'image'];
const APPROVAL_STATUSES: ReadonlyArray<TemplateApprovalStatus> = [
  'draft',
  'pending',
  'approved',
  'rejected',
];

// BE enum 은 대문자(BASIC/APPROVED) → FE 내부 소문자로 정규화.
const asKakaoType = (v: unknown): KakaoTemplateType | null => {
  const lc = typeof v === 'string' ? v.toLowerCase() : '';
  return (KAKAO_TYPES as ReadonlyArray<string>).includes(lc) ? (lc as KakaoTemplateType) : null;
};

const asApproval = (v: unknown): TemplateApprovalStatus | null => {
  const lc = typeof v === 'string' ? v.toLowerCase() : '';
  return (APPROVAL_STATUSES as ReadonlyArray<string>).includes(lc) ? (lc as TemplateApprovalStatus) : null;
};

const asStatus = (v: unknown): TemplateStatus => (v === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE');

const asString = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const asBool = (v: unknown): boolean => v === true;

/**
 * 정합 추가 필드(§3.1)는 be-notif Task#3 전까지 wire 에 없을 수 있다 → 누락 시 null/false 로
 * 정규화(타입 안정). 컴포넌트는 null 이면 '—' 로 렌더(라벨 SSoT). 매핑은 여기 단일 정본.
 */
type ListItemWire = TemplateListItem & Record<string, unknown>;

const normalizeListItem = (raw: ListItemWire): TemplateListItem => ({
  templateId: raw.templateId,
  templateCode: raw.templateCode,
  channel: raw.channel,
  languageCode: raw.languageCode,
  subject: raw.subject ?? null,
  kakaoTemplateType: asKakaoType(raw.kakaoTemplateType),
  approvalStatus: asApproval(raw.approvalStatus),
  isAdvertising: asBool(raw.isAdvertising),
  // ID-226 — light view 에 isHtml 추가(be-notif). wire 부재 시 false 폴백(기존 plain).
  isHtml: asBool(raw.isHtml),
  createdAt: raw.createdAt,
});

type DetailWire = TemplateDetail & Record<string, unknown>;

const normalizeDetail = (raw: DetailWire): TemplateDetail => ({
  templateId: raw.templateId,
  templateCode: raw.templateCode,
  channel: raw.channel,
  languageCode: raw.languageCode,
  subject: raw.subject ?? null,
  body: raw.body,
  variablesJson: raw.variablesJson ?? null,
  status: asStatus(raw.status),
  isAdvertising: asBool(raw.isAdvertising),
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
  kakaoTemplateType: asKakaoType(raw.kakaoTemplateType),
  emphasisTitle: asString(raw.emphasisTitle),
  emphasisSubtitle: asString(raw.emphasisSubtitle),
  imageUrl: asString(raw.imageUrl),
  buttons: fromTemplateButtons(Array.isArray(raw.buttons) ? (raw.buttons as ButtonWire[]) : null),
  reprLinkMobile: asString(raw.reprLinkMobile),
  reprLinkPc: asString(raw.reprLinkPc),
  approvalStatus: asApproval(raw.approvalStatus),
  autoSendEnabled: asBool(raw.autoSendEnabled),
  providerTemplateCode: asString(raw.providerTemplateCode),
  // ID-226 — wire `isHtml`(BE Task#2 V26). be-notif 배포 전엔 wire 부재 → false 폴백(기존 plain).
  isHtml: asBool(raw.isHtml),
});

/** FE 소문자 enum → BE 대문자 wire(create/update 송신용). buttons/기타 그대로. */
const toWirePayload = <T extends { kakaoTemplateType?: KakaoTemplateType | null; approvalStatus?: TemplateApprovalStatus | null }>(
  body: T,
): Record<string, unknown> => {
  const wire: Record<string, unknown> = { ...body };
  if (body.kakaoTemplateType) wire.kakaoTemplateType = body.kakaoTemplateType.toUpperCase();
  if (body.approvalStatus) wire.approvalStatus = body.approvalStatus.toUpperCase();
  return wire;
};

export const notificationTemplatesQueryKey = (filter: TemplateListFilter) =>
  ['admin', 'notification-templates', 'list', filter] as const;

export const notificationTemplateQueryKey = (templateId: string) =>
  ['admin', 'notification-templates', 'detail', templateId] as const;

const invalidateLists = (
  qc: ReturnType<typeof useQueryClient>,
): Promise<void> =>
  qc.invalidateQueries({ queryKey: ['admin', 'notification-templates', 'list'] });

export const useNotificationTemplatesQuery = (filter: TemplateListFilter) =>
  useQuery<TemplateListPage>({
    queryKey: notificationTemplatesQueryKey(filter),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}?${buildListQs(filter)}`);
      const wire = await readEnvelope<{
        items?: ListItemWire[];
        total?: number;
        page?: number;
        size?: number;
      }>(res, '템플릿 목록을 불러오지 못했습니다');
      return {
        items: (wire.items ?? []).map(normalizeListItem),
        total: wire.total ?? 0,
        page: wire.page ?? filter.page,
        size: wire.size ?? filter.size,
      };
    },
    staleTime: 30_000,
  });

export const useNotificationTemplateQuery = (templateId: string | undefined) =>
  useQuery<TemplateDetail>({
    queryKey: notificationTemplateQueryKey(templateId ?? ''),
    queryFn: async () => {
      const res = await adminFetch(`${BASE}/${templateId ?? ''}`);
      const wire = await readEnvelope<DetailWire>(res, '템플릿을 불러오지 못했습니다');
      return normalizeDetail(wire);
    },
    enabled: Boolean(templateId) && templateId !== 'new',
  });

export const useCreateNotificationTemplateMutation = () => {
  const qc = useQueryClient();
  return useMutation<TemplateDetail, TemplateApiError, TemplateCreatePayload>({
    mutationFn: async (body) => {
      const wire = toWirePayload(body);
      const idempotencyKey = idempotencyKeyFor('POST', BASE, wire);
      const res = await adminFetch(BASE, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify(wire),
      });
      return normalizeDetail(await readEnvelope<DetailWire>(res, '템플릿 등록에 실패했습니다'));
    },
    onSuccess: () => {
      void invalidateLists(qc);
    },
  });
};

export interface UpdateTemplateInput {
  templateId: string;
  body: TemplateUpdatePayload;
}

export const useUpdateNotificationTemplateMutation = () => {
  const qc = useQueryClient();
  return useMutation<TemplateDetail, TemplateApiError, UpdateTemplateInput>({
    mutationFn: async ({ templateId, body }) => {
      const wire = toWirePayload(body);
      const idempotencyKey = idempotencyKeyFor('PUT', `${BASE}/${templateId}`, wire);
      const res = await adminFetch(`${BASE}/${templateId}`, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify(wire),
      });
      return normalizeDetail(await readEnvelope<DetailWire>(res, '템플릿 수정에 실패했습니다'));
    },
    onSuccess: async (_data, { templateId }) => {
      await qc.invalidateQueries({ queryKey: notificationTemplateQueryKey(templateId) });
      await invalidateLists(qc);
    },
  });
};

export const useDeleteNotificationTemplateMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, TemplateApiError, string>({
    mutationFn: async (templateId) => {
      const idempotencyKey = idempotencyKeyFor('DELETE', `${BASE}/${templateId}`, null);
      const res = await adminFetch(`${BASE}/${templateId}`, {
        method: 'DELETE',
        headers: { 'Idempotency-Key': idempotencyKey },
      });
      if (res.status === 204) return;
      if (!res.ok) {
        let env: Envelope<unknown> | null = null;
        try {
          env = (await res.json()) as Envelope<unknown>;
        } catch {
          env = null;
        }
        throw new TemplateApiError(
          res.status,
          env?.error?.code,
          env?.error?.message ?? '템플릿 삭제에 실패했습니다',
        );
      }
    },
    onSuccess: () => {
      void invalidateLists(qc);
    },
  });
};
